/**
 * Share cards, canonical URLs and the sitemap (2026-10-03).
 *
 * Every main page has a 1200 x 630 share card served by the app itself: /og/<slug>.jpg in /public, drawn from SHARE_CARDS below by
 * views/ShareCard/image.tsx (regenerate: public/og/README.md). The old meta pointed at https://cadinu.io/web/og/*.jpg, which does not
 * exist there (14 of 14 answered 404).
 *
 * Canonical and og:url are always production. The image origin follows the build: the Stage build (NEXT_PUBLIC_APP_ENV=stage)
 * points its cards at Stage so a Stage preview shows Stage's own images; every other build points at production.
 * No APR or price figures, no contact address, no hosting details on any card.
 */

export const SITE_ORIGIN = 'https://apps.cadinu.io'
export const STAGE_ORIGIN = 'https://stage-apps.cadinu.io'
export const ASSET_ORIGIN = (process.env.NEXT_PUBLIC_APP_ENV ?? '').trim() === 'stage' ? STAGE_ORIGIN : SITE_ORIGIN
/** bump when the card art changes: X and Telegram keep an image per URL for days */
export const OG_VERSION = '2'
export const OG_WIDTH = 1200
export const OG_HEIGHT = 630

export type ShareCardSlug =
  | 'home'
  | 'swap'
  | 'liquidity'
  | 'farms'
  | 'pools'
  | 'markets'
  | 'tokens'
  | 'lock'
  | 'competitions'
  | 'create-competition'
  | 'verified-deploy'
  | 'profile'
  | 'nfts'
  | 'governance'
  | 'prediction'
  | 'legacy'

export type ShareCardArt =
  /** px: drawn size; a 128-160 px source is drawn smaller than a 256 px one so it stays sharp */
  | { kind: 'icon'; file: string; px?: number }
  | { kind: 'card'; file: string }
  | { kind: 'pair'; files: [string, string] }

export interface ShareCard {
  /** the small mono label above the headline */
  label: string
  /** one or two lines, broken by hand so no line ends on a single word */
  headline: [string] | [string, string]
  /** one line of value, no figures */
  line: string
  /** the object that floats in front of the husky: a 3D icon, an NFT character card, or two icons */
  art: ShareCardArt
  /** glow and label colour (brand tokens: ice, gold for rewards only, accent-text violet, success green, muted) */
  accent: string
  /** a personal link drawn in the footer in place of the site name (the profile's Earn card, 2026-10-09); never on the page cards */
  link?: string
}

const ICE = '#7FD4E8'
const GOLD = '#FFC24A'
const VIOLET = '#8C7CFF'
const GREEN = '#39D98A'
const MUTED = '#93A3BF'

export const SHARE_CARDS: Record<ShareCardSlug, ShareCard> = {
  home: {
    label: 'CADINUDEX',
    headline: ['Swap, earn, compete', 'on BNB Chain'],
    line: 'Trade from your own wallet, stake for CBON rewards and join trading competitions.',
    art: { kind: 'pair', files: ['images/brand/icons3d/coin.png', 'cbon-logo.png'] },
    accent: VIOLET,
  },
  swap: {
    label: 'SWAP',
    headline: ['Swap tokens', 'on BNB Chain'],
    line: 'The best route across BNB Chain pools, straight from your own wallet.',
    art: { kind: 'icon', file: 'images/brand/icons3d/coin.png' },
    accent: ICE,
  },
  liquidity: {
    label: 'LIQUIDITY',
    headline: ['Provide liquidity,', 'earn fees'],
    line: 'Add tokens to CADINUdex pools and earn a share of every trade.',
    art: { kind: 'icon', file: 'images/brand/icons3d/gem_stone.png' },
    accent: ICE,
  },
  farms: {
    label: 'FARMS',
    headline: ['Farm CBON with', 'your liquidity'],
    line: 'Stake your CADINUdex liquidity positions and earn CBON rewards.',
    art: { kind: 'icon', file: 'cbon-logo.png' },
    accent: GOLD,
  },
  pools: {
    label: 'STAKING POOLS',
    headline: ['Stake CADINU,', 'earn CBON'],
    line: 'Single-token staking on BNB Chain. Rewards build up with every block.',
    art: { kind: 'icon', file: 'images/brand/icons3d/money_bag.png' },
    accent: GOLD,
  },
  markets: {
    label: 'MARKETS',
    headline: ['Every pool, live'],
    line: 'Prices, volume and liquidity for CADINUdex pools and tokens.',
    art: { kind: 'icon', file: 'images/brand/icons3d/chart_increasing.png' },
    accent: ICE,
  },
  tokens: {
    label: 'TOKENS',
    headline: ['Discover tokens'],
    line: 'Price, pools and trading competitions for every token on CADINUdex.',
    art: { kind: 'icon', file: 'images/brand/icons3d/bar_chart.png' },
    accent: VIOLET,
  },
  lock: {
    label: 'CADINU LOCK',
    headline: ['Locks anyone', 'can verify'],
    line: 'Liquidity and token locks with a verified contract, an unlock date and a certificate.',
    art: { kind: 'icon', file: 'images/brand/icons3d/locked.png' },
    accent: ICE,
  },
  competitions: {
    label: 'TRADING COMPETITIONS',
    headline: ['Trade, climb,', 'share the prize'],
    line: 'Token trading competitions on BNB Chain: the top traders split the prize pool.',
    art: { kind: 'icon', file: 'images/fluent/crown.png', px: 190 },
    accent: GOLD,
  },
  'create-competition': {
    label: 'NEW COMPETITION',
    headline: ['Launch a trading', 'competition'],
    line: 'Pick a pool, set the prize and the winners, fund it from your wallet.',
    art: { kind: 'icon', file: 'images/fluent/party.png', px: 190 },
    accent: GOLD,
  },
  'verified-deploy': {
    label: 'VERIFIED DEPLOY',
    headline: ['Deploy,', 'then prove it'],
    line: 'Deploy a contract from its artifact with your own wallet and verify the bytecode.',
    art: { kind: 'icon', file: 'images/brand/icons3d/check_mark_button.png' },
    accent: GREEN,
  },
  profile: {
    label: 'PROFILE',
    headline: ['Your CADINU', 'profile'],
    line: 'Your name, your NFT avatar, your on-chain activity and your referral link.',
    art: { kind: 'card', file: 'images/brand/nft/futuristic.jpg' },
    accent: VIOLET,
  },
  nfts: {
    label: 'CADINU NFTS',
    headline: ['Choose your', 'character'],
    line: 'The CADINU husky NFT collection on BNB Chain.',
    art: { kind: 'card', file: 'images/brand/nft/business.jpg' },
    accent: VIOLET,
  },
  governance: {
    label: 'GOVERNANCE',
    headline: ['Have your say'],
    line: 'Read CADINU community proposals and vote with your wallet.',
    art: { kind: 'icon', file: 'images/fluent/ballot.png', px: 160 },
    accent: VIOLET,
  },
  prediction: {
    label: 'PREDICTION MARKETS',
    headline: ['Prediction markets', 'in demo mode'],
    line: 'Browse markets and outcomes on CADINUdex. Nothing is traded.',
    art: { kind: 'icon', file: 'images/fluent/crystal.png', px: 160 },
    accent: VIOLET,
  },
  legacy: {
    label: 'LEGACY',
    headline: ['Paused products'],
    // non-breaking spaces keep "CADINU V1 to V2" on one line of the card
    line: 'Claim old Lottery prizes and migrate CADINU\u00a0V1\u00a0to\u00a0V2.',
    art: { kind: 'icon', file: 'images/brand/icons3d/hourglass_not_done.png' },
    accent: MUTED,
  },
}

export const SHARE_CARD_SLUGS = Object.keys(SHARE_CARDS) as ShareCardSlug[]
export const isShareCardSlug = (s: string): s is ShareCardSlug => Object.prototype.hasOwnProperty.call(SHARE_CARDS, s)

export const ogImagePath = (slug: ShareCardSlug) => `/og/${slug}.jpg?v=${OG_VERSION}`
export const ogImageUrl = (slug: ShareCardSlug, origin: string = ASSET_ORIGIN) => `${origin}${ogImagePath(slug)}`
export const canonicalUrl = (path: string) => `${SITE_ORIGIN}${path}`

/**
 * The sitemap: public pages a visitor can read without a wallet (plus /u and the two create pages, which explain themselves before
 * asking for one). Redirect-only paths, wallet-only lists (/liquidity, /add, /find) and paused products other than /legacy stay out.
 */
export const SITEMAP_ROUTES: Array<{ path: string; changefreq: 'hourly' | 'daily' | 'weekly' | 'monthly'; priority: number }> = [
  { path: '/', changefreq: 'daily', priority: 1.0 },
  { path: '/swap', changefreq: 'daily', priority: 0.9 },
  { path: '/info', changefreq: 'hourly', priority: 0.8 },
  { path: '/tokens', changefreq: 'daily', priority: 0.8 },
  { path: '/pools', changefreq: 'daily', priority: 0.8 },
  { path: '/farms', changefreq: 'daily', priority: 0.8 },
  { path: '/trading-competition', changefreq: 'hourly', priority: 0.8 },
  { path: '/trading-competition/create', changefreq: 'monthly', priority: 0.6 },
  { path: '/cadinu-lock', changefreq: 'daily', priority: 0.7 },
  { path: '/cadinu-lock/create', changefreq: 'monthly', priority: 0.5 },
  { path: '/lock/verify', changefreq: 'monthly', priority: 0.5 },
  { path: '/verified-deploy', changefreq: 'monthly', priority: 0.5 },
  { path: '/u', changefreq: 'weekly', priority: 0.5 },
  { path: '/governance', changefreq: 'weekly', priority: 0.4 },
  { path: '/prediction-markets', changefreq: 'weekly', priority: 0.4 },
  // /click-to-win left the sitemap on 2026-10-08 (archived; it redirects to /swap)
  { path: '/legacy', changefreq: 'monthly', priority: 0.3 },
  { path: '/terms-of-service', changefreq: 'monthly', priority: 0.2 },
  // /cad1, /cad1/create, /cad1/launches and /cad1/tx/* stay out: the CAD1 launcher is in testing and its pages are noindex nofollow
]

const xmlEscape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;')

export interface SitemapEntry {
  path: string
  changefreq: 'hourly' | 'daily' | 'weekly' | 'monthly'
  priority: number
  /** ISO date (YYYY-MM-DD or full timestamp); the dynamic pages carry one, the static routes none */
  lastmod?: string
}

/**
 * Site review 59 #10 (2026-10-08): the pages people search for. One entry per registry token (/token/<address>, lastmod = the registry
 * row's updatedAt) and per competition (/trading-competition/<id>; legacy V3 ones under /legacy/<id>; lastmod = the latest of its
 * start and end that has passed, hourly while it runs, monthly once over). Rows without a valid address or id are skipped; the lists
 * come from server/seo/sitemapSources.ts, which answers [] for a source that fails, so the static routes are always served.
 */
export interface SitemapSources {
  tokens: Array<{ token?: string; updatedAt?: string }>
  competitions: Array<{ id?: number; startTime?: number; endTime?: number; status?: string; phase?: string }>
  legacy: Array<{ id?: number; startTime?: number; endTime?: number; status?: string; phase?: string }>
  /** now, in seconds (tests pass a fixed one) */
  now?: number
}
const ADDRESS = /^0x[0-9a-fA-F]{40}$/
const isoDay = (seconds: number) => new Date(seconds * 1000).toISOString().slice(0, 10)
export function dynamicSitemapEntries(src: SitemapSources): SitemapEntry[] {
  const now = src.now ?? Math.floor(Date.now() / 1000)
  const out: SitemapEntry[] = []
  const seen = new Set<string>()
  for (const t of src.tokens ?? []) {
    const address = typeof t?.token === 'string' ? t.token.toLowerCase() : ''
    if (!ADDRESS.test(address) || seen.has(address)) continue
    seen.add(address)
    const lastmod = typeof t.updatedAt === 'string' && !Number.isNaN(Date.parse(t.updatedAt)) ? t.updatedAt.slice(0, 10) : undefined
    out.push({ path: `/token/${address}`, changefreq: 'daily', priority: 0.7, ...(lastmod && { lastmod }) })
  }
  const comp = (c: SitemapSources['competitions'][number], path: (id: number) => string) => {
    if (typeof c?.id !== 'number' || !Number.isInteger(c.id) || c.id < 0) return
    const start = typeof c.startTime === 'number' ? c.startTime : undefined
    const end = typeof c.endTime === 'number' ? c.endTime : undefined
    const over = end !== undefined && end <= now
    const last = over ? end : start !== undefined && start <= now ? start : undefined
    out.push({ path: path(c.id), changefreq: over ? 'monthly' : 'hourly', priority: over ? 0.5 : 0.8, ...(last !== undefined && { lastmod: isoDay(last) }) })
  }
  for (const c of src.competitions ?? []) comp(c, (id) => `/trading-competition/${id}`)
  for (const c of src.legacy ?? []) comp(c, (id) => `/trading-competition/legacy/${id}`)
  return out
}

export function sitemapXml(origin: string = SITE_ORIGIN, extra: SitemapEntry[] = []): string {
  const urls = ([...SITEMAP_ROUTES, ...extra] as SitemapEntry[]).map(
    (r) =>
      `  <url><loc>${xmlEscape(`${origin}${r.path}`)}</loc>${r.lastmod ? `<lastmod>${xmlEscape(r.lastmod)}</lastmod>` : ''}<changefreq>${r.changefreq}</changefreq><priority>${r.priority.toFixed(1)}</priority></url>`,
  )
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls.join('\n')}\n</urlset>\n`
}
